import { can, type AdminRole, type Permission } from '../admin/shared/permissions'
import type { ErpSettings, FrenteKey } from './contract'
import { STAFF_LEAD_ROLES } from './xp/staff/contract'

/* ERP · frentes e experiências — o Staff organizado pelas 6 EXPERIÊNCIAS do overview (CFB Cognitive | AI Experience):
   Sponsor, Staff, Corporate, Faith-communities, Ambassadors e OSP. Cada experiência é um item do menu; a 1ª tela dela é a
   visão (/staff/xp/<chave>) e as outras viram uma árvore no menu lateral (EXPERIENCE_TREES; no celular e com o menu
   recolhido, abas no topo). A frente da pessoa (pelo perfil) decide QUAIS experiências e telas ela vê e por qual
   experiência entra; só a Tecnologia vê tudo (e a engrenagem "Configurações").
   Menus e "vê dado pessoal" podem ser mudados em Configurações › Frentes e telas (ErpSettings.frentes).
   Este arquivo fica sem React (o servidor valida as Configurações com ele); os ícones estão em ui/experience-icons.ts. */

/** Catálogo de telas do Staff: chave → rótulo, endereço e a permissão da área (quando a tela é de uma área existente). */
export type ErpView = { key: string; label: string; path: string; permission?: Permission }

const v = (key: string, label: string, path: string, permission?: Permission): ErpView => ({ key, label, path, permission })

export const ERP_VIEWS: Record<string, ErpView> = Object.fromEntries([
    v('inicio', 'Início', '/staff/inicio'),
    v('hoje', 'Hoje', '/staff/hoje'),
    // Visão de cada experiência (/staff/xp/<chave>)
    v('xp-sponsor', 'Visão', '/staff/xp/sponsor'),
    v('xp-staff', 'Visão', '/staff/xp/staff'),
    v('xp-corporate', 'Visão', '/staff/xp/corporate'),
    v('xp-faith', 'Visão', '/staff/xp/faith'),
    v('xp-ambassadors', 'Visão', '/staff/xp/ambassadors'),
    v('xp-osp', 'Visão', '/staff/xp/osp'),
    // Telas novas do ERP (/staff/erp/<chave>)
    v('rel-visao', 'Visão geral', '/staff/erp/rel-visao'),
    v('doadores', 'Todos os doadores', '/staff/erp/doadores'),
    v('sp-criancas', 'Crianças dos Sponsor', '/staff/erp/sp-criancas'),
    v('sp-atividade', 'Atividade no app', '/staff/erp/sp-atividade'),
    v('inadimplencia', 'Inadimplência', '/staff/erp/inadimplencia'),
    v('ocorrencias', 'Ocorrências', '/staff/erp/ocorrencias'),
    v('fin-visao', 'Faturas do mês', '/staff/erp/fin-visao'),
    v('repasses', 'Repasse às OSPs', '/staff/erp/repasses'),
    v('diretoria', 'Painel da diretoria', '/staff/erp/diretoria'),
    v('igrejas', 'Cadastro de igrejas', '/staff/erp/igrejas'),
    v('eventos', 'Live Concerts e eventos', '/staff/erp/eventos'),
    v('consultores', 'Consultores', '/staff/erp/consultores'),
    v('empresas', 'Empresas e projetos', '/staff/erp/empresas'),
    v('osps', 'OSPs e projetos', '/staff/erp/osps'),
    v('criancas', 'Crianças', '/staff/erp/criancas'),
    v('mkt-visao', 'Origem dos doadores', '/staff/erp/mkt-visao'),
    // Telas que já existiam no Staff
    v('pre-ativos', 'Pré-ativos e boas-vindas', '/staff/pre-ativos', 'retencao'),
    v('retencao', 'Cancelamento e retenção', '/staff/retencao', 'retencao'),
    v('recorrencias', 'Recorrências e pagamentos', '/staff/recorrencias', 'recorrencias.ver'),
    v('conciliacao', 'Conciliação Braspag × CRM', '/staff/conciliacao', 'conciliacao'),
    v('moderacao', 'Moderação de mensagens', '/staff/moderacao', 'moderacao'),
    v('cartas', 'Cartas e prazos', '/staff/cartas', 'cartas'),
    v('ver-como', 'Ver como padrinho', '/staff/ver-como', 'ver_como'),
    v('fichas-live', 'Fichas digitais', '/staff/fichas', 'fichas.ver'),
    v('campo', 'Campo e eventos', '/staff/parceiros', 'parceiros'),
    v('inscricoes', 'Inscrição de crianças', '/staff/inscricoes', 'inscricoes'),
    v('mkt-digital', 'Campanhas e ROI', '/staff/marketing', 'marketing'),
    v('indicadores', 'Indicadores da base', '/staff/indicadores', 'indicadores'),
    v('igr-visao', 'Visão geral', '/staff/igrejas/visao-geral', 'parceiros'),
    v('igr-parceiras', 'Igrejas parceiras', '/staff/igrejas/parceiras', 'parceiros'),
    v('igr-busca', 'Busca no mapa', '/staff/igrejas/busca', 'parceiros'),
    v('igr-regua', 'Régua de abordagem', '/staff/igrejas/regua', 'parceiros'),
    v('igr-naoaprov', 'Não aprovadas', '/staff/igrejas/nao-aprovadas', 'parceiros'),
    v('igr-buscadas', 'Igrejas já buscadas', '/staff/igrejas/ja-buscadas', 'parceiros'),
    v('emp-visao', 'Visão geral', '/staff/empresas/visao-geral', 'parceiros'),
    v('emp-atuais', 'Empresas atuais', '/staff/empresas/atuais', 'parceiros'),
    v('emp-busca', 'Motor de busca', '/staff/empresas/busca', 'parceiros'),
    v('emp-regua', 'Régua de abordagem', '/staff/empresas/regua', 'parceiros'),
    v('emp-naoaprov', 'Não aprovadas', '/staff/empresas/nao-aprovadas', 'parceiros'),
    v('emb-visao', 'Visão geral', '/staff/embaixadores/visao-geral', 'embaixadores'),
    v('emb-atuais', 'Embaixadores atuais', '/staff/embaixadores/atuais', 'embaixadores'),
    v('emb-busca', 'Motor de busca', '/staff/embaixadores/busca', 'embaixadores'),
    v('emb-regua', 'Régua de relacionamento', '/staff/embaixadores/regua', 'embaixadores'),
    v('emb-aprovacao', 'Aprovação do cliente', '/staff/embaixadores/aprovacao', 'embaixadores'),
    v('emb-naoaprov', 'Não aprovados', '/staff/embaixadores/nao-aprovados', 'embaixadores'),
    v('emb-buscados', 'Já buscados', '/staff/embaixadores/ja-buscados', 'embaixadores'),
    v('emb-portal', 'Portal do cliente', '/staff/embaixadores/portal', 'embaixadores'),
].map(x => [x.key, x]))

// ---------- experiências ----------

export const EXPERIENCE_KEYS = ['sponsor', 'staff', 'corporate', 'faith', 'ambassadors', 'osp'] as const
export type ExperienceKey = (typeof EXPERIENCE_KEYS)[number]

/** Experiência: nome oficial (em inglês, como no overview), o público em português, a frase-guia e as telas (a 1ª é a visão). */
export type ExperienceDef = { key: ExperienceKey; label: string; pt: string; tagline: string; overview: string; views: readonly string[] }

const xp = (key: ExperienceKey, label: string, pt: string, tagline: string, views: readonly string[]): ExperienceDef =>
    ({ key, label, pt, tagline, overview: `xp-${key}`, views: [`xp-${key}`, ...views] })

/** As 6 experiências, na ordem do menu. Toda tela do catálogo (menos o Início) é de exatamente uma experiência. */
export const EXPERIENCES: Record<ExperienceKey, ExperienceDef> = {
    sponsor: xp('sponsor', 'Sponsor', 'Padrinhos e doadores', 'Cada padrinho: de onde veio, como está e o que precisa para ficar.',
        ['rel-visao', 'doadores', 'sp-criancas', 'sp-atividade', 'pre-ativos', 'inadimplencia', 'retencao', 'ocorrencias', 'cartas', 'moderacao', 'ver-como', 'fin-visao', 'recorrencias', 'conciliacao', 'mkt-visao']),
    staff: xp('staff', 'Staff', 'A equipe ChildFund', 'O dia de cada frente, o time e os agentes que trabalham junto.', ['hoje', 'diretoria', 'indicadores']),
    corporate: xp('corporate', 'Corporate', 'Empresas e institutos', 'Empresas parceiras, projetos, funcionários padrinhos e a régua de novas empresas.',
        ['emp-visao', 'emp-atuais', 'emp-busca', 'emp-regua', 'emp-naoaprov', 'empresas']),
    faith: xp('faith', 'Faith-communities', 'Comunidades de fé', 'Igrejas parceiras, Live Concerts, consultores e quem veio de cada uma.',
        ['igr-visao', 'igr-parceiras', 'igr-busca', 'igr-regua', 'igr-naoaprov', 'igr-buscadas', 'eventos', 'fichas-live', 'consultores', 'campo', 'igrejas']),
    ambassadors: xp('ambassadors', 'Ambassadors', 'Embaixadores', 'Quem clicou no link de cada embaixador, quem virou padrinho e quantas crianças.',
        ['emb-visao', 'emb-atuais', 'emb-busca', 'emb-regua', 'emb-aprovacao', 'emb-naoaprov', 'emb-buscados', 'emb-portal', 'mkt-digital']),
    osp: xp('osp', 'OSP', 'Organizações parceiras', 'Crianças, cartas, subsídio e prestação de contas de cada organização local.', ['osps', 'criancas', 'inscricoes', 'repasses']),
}

export const isExperienceKey = (k: string): k is ExperienceKey => (EXPERIENCE_KEYS as readonly string[]).includes(k)

/** Experiência de uma tela do catálogo (null = Início ou chave desconhecida). */
export function experienceOfView(key: string): ExperienceDef | null {
    for (const k of EXPERIENCE_KEYS) if (EXPERIENCES[k].views.includes(key)) return EXPERIENCES[k]
    return null
}

/** Experiência pelo nome da área do menu (as áreas do menu são os nomes das experiências). */
export function experienceOfArea(area: string): ExperienceDef | null {
    return EXPERIENCE_KEYS.map(k => EXPERIENCES[k]).find(e => e.label === area) ?? null
}

// ---------- árvore das experiências ----------

/** Nó da árvore de uma experiência no menu lateral: rótulo próprio (não o do catálogo), a tela que abre (opcional) e os
    galhos. Um nó com tela e galhos também é clicável. `tab` = uma aba da tela (/staff/xp/<chave>?tab=<aba>; sem `tab`, a
    1ª aba); `roles` = só esses perfis veem o nó (ex.: Staff › Equipe, só lideranças). */
export type XpTreeNode = { label: string; view?: string; tab?: string; roles?: readonly AdminRole[]; children?: readonly XpTreeNode[] }

const n = (label: string, view?: string, children?: readonly XpTreeNode[]): XpTreeNode => ({ label, view, children })
const tab = (label: string, view: string, id: string, roles?: readonly AdminRole[]): XpTreeNode => ({ label, view, tab: id, roles })

/** Experiências que trocam as abas do topo por uma árvore no menu lateral (todas: o Sponsor espelha o Sponsor App, o PWA
    do padrinho; o Staff põe as abas próprias da visão como nós; as outras, as telas agrupadas). Fora do Staff, a visão
    (Hoje · A IA fez · Carteira) saiu do menu (pedido do usuário em 30/10): o item da experiência abre o 1º nó da árvore e
    a visão só abre pelo endereço (/staff/xp/<chave>). Cada tela aparece no máximo uma vez como nó comum (as abas podem repetir a tela) e só telas da própria
    experiência; as que ficarem de fora (ex.: uma tela posta pelas Configurações) entram no fim da árvore (staff-nav.ts). */
export const EXPERIENCE_TREES: Partial<Record<ExperienceKey, readonly XpTreeNode[]>> = {
    sponsor: [
        n('Sponsor App', 'rel-visao', [
            n('Doadores', 'doadores', [n('Novos', 'pre-ativos'), n('Em risco', 'retencao'), n('De onde vieram', 'mkt-visao')]),
            n('Crianças dos Sponsor', 'sp-criancas'),
            n('Atividade no app', 'sp-atividade'),
            n('Mensagens', 'moderacao', [n('Cartas e prazos', 'cartas')]),
            n('Pagamentos', 'recorrencias', [n('Inadimplência', 'inadimplencia'), n('Faturas do mês', 'fin-visao'), n('Conciliação', 'conciliacao')]),
            n('Atendimento', 'ocorrencias'),
            n('Ver como padrinho', 'ver-como'),
        ]),
    ],
    staff: [
        n('Meu dia', 'xp-staff'),
        tab('Minha jornada', 'xp-staff', 'jornada'),
        tab('Pergunte', 'xp-staff', 'pergunte'),
        tab('Conhecimento', 'xp-staff', 'conhecimento'),
        tab('Equipe', 'xp-staff', 'equipe', STAFF_LEAD_ROLES),
        n('Pendências de hoje', 'hoje'),
        n('Gestão', undefined, [n('Painel da diretoria', 'diretoria'), n('Indicadores', 'indicadores')]),
    ],
    corporate: [
        n('Empresas', 'emp-visao', [n('Empresas atuais', 'emp-atuais'), n('Empresas e projetos', 'empresas')]),
        n('Prospecção', 'emp-busca', [n('Régua de abordagem', 'emp-regua'), n('Não aprovadas', 'emp-naoaprov')]),
    ],
    faith: [
        n('Igrejas', 'igr-visao', [n('Igrejas parceiras', 'igr-parceiras'), n('Cadastro de igrejas', 'igrejas')]),
        n('Prospecção', 'igr-busca', [n('Régua de abordagem', 'igr-regua'), n('Não aprovadas', 'igr-naoaprov'), n('Igrejas já buscadas', 'igr-buscadas')]),
        n('Live Concerts', 'eventos', [n('Fichas digitais', 'fichas-live')]),
        n('Campo', 'campo', [n('Consultores', 'consultores')]),
    ],
    ambassadors: [
        n('Embaixadores', 'emb-visao', [n('Embaixadores atuais', 'emb-atuais')]),
        n('Prospecção', 'emb-busca', [n('Régua de relacionamento', 'emb-regua'), n('Aprovação do cliente', 'emb-aprovacao'), n('Não aprovados', 'emb-naoaprov'), n('Já buscados', 'emb-buscados')]),
        n('Campanhas e ROI', 'mkt-digital'),
        n('Portal do cliente', 'emb-portal'),
    ],
    osp: [
        n('OSPs e projetos', 'osps'),
        n('Crianças', 'criancas', [n('Inscrição de crianças', 'inscricoes')]),
        n('Repasse às OSPs', 'repasses'),
    ],
}

/** O perfil vê este nó? (nó sem `roles` = todos) */
export const treeNodeAllowed = (node: XpTreeNode, role: AdminRole | null | undefined): boolean => !node.roles || (!!role && node.roles.includes(role))

/** Nós de aba da árvore de uma experiência (todos os níveis), na ordem da árvore. */
export function experienceTreeTabs(key: ExperienceKey): XpTreeNode[] {
    const walk = (nodes: readonly XpTreeNode[]): XpTreeNode[] => nodes.flatMap(node => [...(node.tab ? [node] : []), ...walk(node.children ?? [])])
    return walk(EXPERIENCE_TREES[key] ?? [])
}

const AMBIGUOUS_LABEL = /^(Visão|Motor de busca|Régua|Não aprovad|Busca no mapa)/

/** Nome da tela fora do menu (Configurações, histórico): nomes genéricos levam a experiência ("Sponsor · Visão"). */
export function viewFullLabel(key: string): string {
    const v = ERP_VIEWS[key]
    if (!v) return key
    const e = AMBIGUOUS_LABEL.test(v.label) ? experienceOfView(key) : null
    return e ? `${e.label} · ${v.label}` : v.label
}

// ---------- frentes ----------

/** Menu: [área ('' = item solto), chaves das telas]. As telas de uma área viram abas no topo da tela (ou a árvore no
    menu lateral, nas experiências de EXPERIENCE_TREES). */
export type NavGroup = readonly [area: string, keys: readonly string[]]

/** `entry`: a tela por onde a frente entra no Staff (/staff e o login levam a ela). */
export type FrenteDef = { key: FrenteKey; label: string; role: string; pii: boolean; entry: string; nav: readonly NavGroup[] }

/** Menu de uma frente pelas experiências: { experiência: telas } (true = todas), sempre na ordem das experiências. */
function xpNav(access: Partial<Record<ExperienceKey, true | readonly string[]>>): NavGroup[] {
    return EXPERIENCE_KEYS.filter(k => access[k]).map(k => {
        const a = access[k]!
        return [EXPERIENCES[k].label, a === true ? EXPERIENCES[k].views : a] as const
    })
}

const STAFF_DAY = ['xp-staff', 'hoje'] as const

export const FRENTES: Record<FrenteKey, FrenteDef> = {
    relacionamento: {
        key: 'relacionamento', label: 'Relacionamento', role: 'Atendimento, Apadrinhamento e Retenção', pii: true, entry: 'xp-sponsor',
        nav: xpNav({ sponsor: ['xp-sponsor', 'rel-visao', 'doadores', 'sp-criancas', 'sp-atividade', 'pre-ativos', 'inadimplencia', 'retencao', 'ocorrencias', 'cartas', 'moderacao', 'ver-como'], staff: STAFF_DAY }),
    },
    captacao: { key: 'captacao', label: 'Captação eclesiástica', role: 'Igrejas, Live Concert e consultores', pii: false, entry: 'xp-faith', nav: xpNav({ faith: true, staff: STAFF_DAY }) },
    corporativo: { key: 'corporativo', label: 'Corporativo', role: 'Empresas e institutos', pii: false, entry: 'xp-corporate', nav: xpNav({ corporate: true, osp: ['xp-osp', 'osps'], staff: STAFF_DAY }) },
    marketing: {
        key: 'marketing', label: 'Marketing', role: 'Marketing e Comunicação', pii: false, entry: 'xp-ambassadors',
        nav: xpNav({ ambassadors: true, sponsor: ['xp-sponsor', 'mkt-visao', 'doadores'], staff: STAFF_DAY }),
    },
    programas: {
        key: 'programas', label: 'Programas', role: 'Programas e Proteção', pii: false, entry: 'xp-osp',
        nav: xpNav({ osp: ['xp-osp', 'osps', 'criancas', 'inscricoes'], sponsor: ['xp-sponsor', 'sp-criancas', 'cartas', 'moderacao'], staff: STAFF_DAY }),
    },
    financeiro: {
        key: 'financeiro', label: 'Financeiro', role: 'Finanças e Faturamento', pii: true, entry: 'xp-sponsor',
        nav: xpNav({ sponsor: ['xp-sponsor', 'fin-visao', 'recorrencias', 'conciliacao', 'inadimplencia'], osp: ['xp-osp', 'repasses'], staff: STAFF_DAY }),
    },
    // Gestão: a visão de cada experiência + a diretoria e os painéis gerais (sem as telas operacionais)
    gestao: {
        key: 'gestao', label: 'Gestão', role: 'Diretoria e SMT', pii: true, entry: 'xp-staff',
        nav: xpNav({
            sponsor: ['xp-sponsor', 'rel-visao', 'doadores', 'sp-criancas', 'sp-atividade', 'retencao', 'fin-visao', 'mkt-visao'], staff: ['xp-staff', 'diretoria', 'indicadores'],
            corporate: ['xp-corporate', 'emp-visao'], faith: ['xp-faith', 'igr-visao', 'eventos'], ambassadors: ['xp-ambassadors', 'emb-visao'], osp: ['xp-osp', 'osps', 'repasses'],
        }),
    },
    // Tecnologia: tudo liberado (menu completo) + Configurações
    tecnologia: {
        key: 'tecnologia', label: 'Tecnologia', role: 'TI · administração do sistema', pii: false, entry: 'xp-staff',
        nav: xpNav({ sponsor: true, staff: true, corporate: true, faith: true, ambassadors: true, osp: true }),
    },
}

/** Frentes que cada perfil pode abrir (a primeira é a de entrada). O admin (Tecnologia) abre todas. */
export const ROLE_FRENTES: Record<AdminRole, readonly FrenteKey[]> = {
    admin: ['tecnologia', 'gestao', 'relacionamento', 'captacao', 'corporativo', 'marketing', 'programas', 'financeiro'],
    gerencia: ['gestao', 'relacionamento', 'captacao', 'corporativo', 'marketing', 'programas', 'financeiro'],
    financeiro: ['financeiro'],
    eventos: ['captacao'],
    relacionamento: ['relacionamento'],
    moderacao: ['programas'],
    programas: ['programas'],
    marketing: ['marketing'],
    corporativo: ['corporativo'],
    leitura: ['gestao'],
}

/** Item do menu já resolvido: telas que o perfil pode abrir, agrupadas por área. */
export type NavEntry = { area: string; view: ErpView }

/** Menu efetivo de uma frente: padrão ou o das Configurações, sem as telas que o perfil não abre. */
export function frenteNav(frente: FrenteKey, role: AdminRole, settings?: ErpSettings | null): NavEntry[] {
    const def = FRENTES[frente]
    const custom = settings?.frentes[frente]?.nav
    const groups: { area: string; keys: readonly string[] }[] = custom
        ? regroup(def.nav, custom)
        : def.nav.map(([area, keys]) => ({ area, keys }))
    const out: NavEntry[] = []
    for (const g of groups) {
        for (const key of g.keys) {
            const view = ERP_VIEWS[key]
            if (!view || (view.permission && !can(role, view.permission))) continue
            out.push({ area: g.area, view })
        }
    }
    return out
}

/** Menu das Configurações (lista de chaves, na ordem) → cada tela vai para a área do padrão da frente ou, se não estiver
    nele, para a sua experiência (o Início fica solto); chave sem lugar vai para "Mais". */
function regroup(nav: readonly NavGroup[], keys: readonly string[]) {
    const areaOf = new Map<string, string>([['inicio', '']])
    for (const k of Object.keys(ERP_VIEWS)) {
        const e = experienceOfView(k)
        if (e) areaOf.set(k, e.label)
    }
    for (const [area, ks] of nav) for (const k of ks) areaOf.set(k, area)
    const groups: { area: string; keys: string[] }[] = []
    for (const k of keys) {
        const area = areaOf.get(k) ?? 'Mais'
        let g = groups.find(x => x.area === area)
        if (!g) groups.push(g = { area, keys: [] })
        g.keys.push(k)
    }
    return groups
}

/** A frente vê CPF, e-mail e celular (mascarados até "Mostrar")? */
export function frentePii(frente: FrenteKey, settings?: ErpSettings | null): boolean {
    return settings?.frentes[frente]?.pii ?? FRENTES[frente].pii
}

export function frentesOf(role: AdminRole): readonly FrenteKey[] {
    return ROLE_FRENTES[role]
}

/** Área (e as telas dela) a que um endereço pertence, para desenhar as abas. */
export function areaOfPath(nav: NavEntry[], pathname: string): { area: string; tabs: ErpView[]; current: ErpView } | null {
    const hit = nav.find(e => pathname === e.view.path || pathname.startsWith(`${e.view.path}/`))
    if (!hit || !hit.area) return null
    return { area: hit.area, tabs: nav.filter(e => e.area === hit.area).map(e => e.view), current: hit.view }
}

/** Endereço por onde a frente entra (/staff, login, troca de frente): a experiência de entrada se estiver no menu efetivo;
    senão a 1ª tela do menu; sem nenhuma, o Início. */
export function frenteEntry(frente: FrenteKey, nav: readonly NavEntry[]): string {
    const entry = FRENTES[frente].entry
    return (nav.find(e => e.view.key === entry) ?? nav.find(e => e.area) ?? nav[0])?.view.path ?? ERP_VIEWS.inicio!.path
}

/** Telas de uma experiência no menu efetivo da pessoa (na ordem do menu). */
export function experienceViews(nav: readonly NavEntry[], key: ExperienceKey): ErpView[] {
    const label = EXPERIENCES[key].label
    return nav.filter(e => e.area === label).map(e => e.view)
}
